import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { SPONSOR_LOCALES, type SponsorLocale } from '@/lib/contract'
import { platform } from '@/platform'
import ptBR from './locales/pt-BR.json'
import { DEFAULT_LOCALE, LANGUAGE_STORAGE_KEY, resolveInitialLocale, isSponsorLocale } from './locale'

/* Spec PWA-001 — i18n do App do Padrinho (pt-BR, en-US, es-ES, de-DE, ja-JP).
   Chaves com namespace no primeiro nivel: t('sponsorAuth.enter.submit').
   So o pt-BR (idioma padrao e fallback) vai no pacote inicial; os demais catalogos sao baixados sob demanda,
   para o JS inicial ficar dentro do orcamento (SC-006) sem crescer a cada idioma novo. */

const LOADERS: Record<Exclude<SponsorLocale, 'pt-BR'>, () => Promise<{ default: Record<string, unknown> }>> = {
    'en-US': () => import('./locales/en-US.json'),
    'es-ES': () => import('./locales/es-ES.json'),
    'de-DE': () => import('./locales/de-DE.json'),
    'ja-JP': () => import('./locales/ja-JP.json'),
}

async function ensureLocale(locale: SponsorLocale): Promise<void> {
    if (locale === 'pt-BR' || i18n.hasResourceBundle(locale, 'translation')) return
    const catalog = await LOADERS[locale]()
    i18n.addResourceBundle(locale, 'translation', catalog.default, true, true)
}

export function hasStoredLocale(): boolean {
    return isSponsorLocale(platform.storage.get(LANGUAGE_STORAGE_KEY))
}

/* Troca imediata + persistencia no aparelho (FR-036). Salvar na identidade e responsabilidade de quem chama. */
export async function changeLocale(locale: SponsorLocale): Promise<void> {
    await ensureLocale(locale)
    platform.storage.set(LANGUAGE_STORAGE_KEY, locale)
    await i18n.changeLanguage(locale)
}

const initialLocale = resolveInitialLocale(platform.storage.get(LANGUAGE_STORAGE_KEY), platform.deviceLanguages())

/* Resolve quando o idioma inicial esta pronto; o main.tsx aguarda antes de renderizar.
   Falha ao baixar o catalogo (offline no 1o acesso) cai no pt-BR em vez de travar o app. */
export const i18nReady: Promise<void> = i18n.use(initReactI18next).init({
    resources: { 'pt-BR': { translation: ptBR } },
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SPONSOR_LOCALES],
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnNull: false,
}).then(async () => {
    if (initialLocale === DEFAULT_LOCALE) return
    try {
        await ensureLocale(initialLocale)
        await i18n.changeLanguage(initialLocale)
    } catch {
        /* segue em pt-BR */
    }
})

document.documentElement.lang = i18n.language
i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng
})

export default i18n
